import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { Button } from "./ui/button";
import { Target, X, Download, Check, Loader2, Users, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * GoalHitCelebration
 * ------------------
 * Full-screen "🎯 GOAL HIT" moment that fires the first time a user crosses
 * their body-weight goal. Triggered by the backend via the `weight:goal_hit`
 * WebSocket event (also a web-push for backgrounded tabs).
 *
 * Design parity with `ChallengeWinCelebration`:
 *   - bouncing icon + pulsing headline + scoreboard
 *   - 1080x1920 canvas-generated share card
 *   - Facebook / Instagram / X share buttons
 *   - "Save All" download + caption-copy
 *
 * Dismissal is local — the backend already wrote `goal_hit_at` server-side
 * so reloads won't re-trigger.
 */
const APP_URL = "https://gymwithgrizz.app";
const APP_HASHTAG = "#gymwithgrizz";

export default function GoalHitCelebration() {
  const { subscribe } = useWebSocket();
  const { user } = useAuth();
  const [hit, setHit] = useState(null); // { goal_weight_kg, weight_kg, previous_weight_kg, direction, post_id }
  const [postHidden, setPostHidden] = useState(false);
  const [hidingPost, setHidingPost] = useState(false);
  const canvasRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Listen for goal_hit WS event (sent only to the goal-hitter themselves).
  useEffect(() => {
    const unsub = subscribe("weight:goal_hit", (msg) => {
      if (!user || msg.user_id !== user.user_id) return;
      setHit({
        goal_weight_kg: Number(msg.goal_weight_kg),
        weight_kg: Number(msg.weight_kg),
        previous_weight_kg: msg.previous_weight_kg != null ? Number(msg.previous_weight_kg) : null,
        direction: msg.direction || "lose",
        post_id: msg.post_id || null,
      });
      setPostHidden(false);
    });
    return () => unsub && unsub();
    // eslint-disable-next-line
  }, [subscribe, user?.user_id]);

  const meta = useMemo(() => {
    if (!hit) return null;
    const isLose = hit.direction === "lose";
    const diff = hit.previous_weight_kg != null
      ? Number(Math.abs(hit.previous_weight_kg - hit.weight_kg).toFixed(1))
      : null;
    return {
      goal: hit.goal_weight_kg,
      current: hit.weight_kg,
      diff,
      label: isLose ? "Lost" : hit.direction === "gain" ? "Gained" : "Hit",
      verb: isLose ? "lose" : hit.direction === "gain" ? "gain" : "reach",
    };
  }, [hit]);

  // Generate share card.
  useEffect(() => {
    if (!hit || !meta || !canvasRef.current) {
      setImageUrl(null);
      return;
    }
    setGenerating(true);
    const c = canvasRef.current;
    c.width = 1080;
    c.height = 1920;
    const ctx = c.getContext("2d");

    // Background
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, 1080, 1920);
    // Emerald diagonal stripes for goal moment
    for (let i = -1920; i < 1920; i += 240) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 1920, 1920);
      ctx.lineWidth = 60;
      ctx.strokeStyle = "rgba(16, 185, 129, 0.06)";
      ctx.stroke();
    }
    // Top/bottom bars (emerald)
    ctx.fillStyle = "#10b981";
    ctx.fillRect(0, 0, 1080, 14);
    ctx.fillRect(0, 1906, 1080, 14);

    // Bullseye emoji
    ctx.font = "bold 360px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("🎯", 540, 480);

    // Headline
    ctx.fillStyle = "#10b981";
    ctx.font = "bold 140px sans-serif";
    ctx.fillText("GOAL HIT", 540, 700);

    // Sub
    ctx.fillStyle = "#ffffff";
    ctx.font = "54px sans-serif";
    ctx.fillText(`Target: ${meta.goal} KG`, 540, 800);

    // Box with current weight
    const boxY = 920;
    const boxH = 380;
    ctx.fillStyle = "rgba(255, 255, 255, 0.04)";
    ctx.fillRect(80, boxY, 920, boxH);
    ctx.strokeStyle = "#262626";
    ctx.lineWidth = 4;
    ctx.strokeRect(80, boxY, 920, boxH);
    // Highlight column
    ctx.fillStyle = "rgba(16, 185, 129, 0.15)";
    ctx.fillRect(80, boxY, 460, boxH);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText("NOW", 310, boxY + 90);
    ctx.fillStyle = "#10b981";
    ctx.font = "bold 180px sans-serif";
    ctx.fillText(`${meta.current}`, 310, boxY + 280);
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText("kg", 310, boxY + 350);

    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText("GOAL", 770, boxY + 90);
    ctx.fillStyle = "#737373";
    ctx.font = "bold 180px sans-serif";
    ctx.fillText(`${meta.goal}`, 770, boxY + 280);
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText("kg", 770, boxY + 350);

    if (meta.diff != null) {
      ctx.fillStyle = "#10b981";
      ctx.font = "bold 64px sans-serif";
      const sign = meta.label === "Lost" ? "-" : "+";
      ctx.fillText(`${sign}${meta.diff} kg`, 540, 1450);
      ctx.fillStyle = "#a3a3a3";
      ctx.font = "36px sans-serif";
      ctx.fillText(`${meta.label.toUpperCase()} TO HIT GOAL`, 540, 1510);
    }

    // Branding
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 80px sans-serif";
    ctx.fillText("GYM with GRIZZ", 540, 1620);
    ctx.fillStyle = "#10b981";
    ctx.font = "44px sans-serif";
    ctx.fillText(APP_URL.replace(/^https?:\/\//, ""), 540, 1700);
    ctx.fillStyle = "#737373";
    ctx.font = "36px sans-serif";
    ctx.fillText(APP_HASHTAG, 540, 1780);

    c.toBlob((blob) => {
      if (blob) setImageUrl(URL.createObjectURL(blob));
      setGenerating(false);
    }, "image/png", 0.92);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit, meta]);

  if (!hit || !meta) return null;

  const caption = meta.diff != null
    ? `Hit my body-weight goal of ${meta.goal}kg — ${meta.label.toLowerCase()} ${meta.diff}kg to get here. 🎯 #gymwithgrizz`
    : `Hit my body-weight goal of ${meta.goal}kg. 🎯 #gymwithgrizz`;

  const close = () => {
    setHit(null);
    if (imageUrl) try { URL.revokeObjectURL(imageUrl); } catch {}
    setImageUrl(null);
  };

  const downloadImage = async (showToast = true) => {
    if (!imageUrl) return null;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `gymwithgrizz-goal-hit.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (showToast) toast.success("Image saved");
    return imageUrl;
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied");
    } catch { /* ignore */ }
  };

  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_URL)}&quote=${encodeURIComponent(caption)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");
    downloadImage(false);
  };
  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(APP_URL)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
    downloadImage(false);
  };
  const shareInstagram = async () => {
    if (navigator.share && imageUrl) {
      try {
        const blob = await (await fetch(imageUrl)).blob();
        const file = new File([blob], "gymwithgrizz-goal-hit.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: caption, title: "Goal hit" });
          return;
        }
      } catch { /* user cancelled / unsupported */ }
    }
    await downloadImage(false);
    await copyCaption();
    toast.success("Image saved + caption copied — open Instagram and paste");
  };
  const shareAll = async () => {
    await downloadImage(false);
    await copyCaption();
    toast.success("Image saved + caption copied — share to any app", { duration: 5000 });
  };

  const hidePost = async () => {
    if (!hit?.post_id || postHidden) return;
    setHidingPost(true);
    try {
      await api.delete(`/feed/${hit.post_id}`);
      setPostHidden(true);
      toast.success("Post removed from your Squad");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't hide post");
    } finally {
      setHidingPost(false);
    }
  };

  return (
    <div
      data-testid="goal-hit-overlay"
      className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="max-w-md w-full text-center">
        <button
          data-testid="goal-hit-close"
          onClick={close}
          className="absolute top-4 right-4 text-neutral-500 hover:text-white p-2"
          title="Close"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="animate-bounce text-7xl mb-3">🎯</div>
        <div className="font-display text-5xl text-emerald-400 uppercase tracking-tight animate-pulse mb-1">
          Goal Hit!
        </div>
        <div className="text-sm uppercase tracking-widest text-neutral-400 mb-6">
          You {meta.verb === "lose" ? "lost" : meta.verb === "gain" ? "gained" : "reached"} your way to <span className="text-white">{meta.goal} kg</span>
        </div>

        <div className="grid grid-cols-2 divide-x divide-neutral-800 border border-neutral-800 bg-neutral-900/40 mb-3">
          <div className="p-4 bg-emerald-500/10">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Now</div>
            <div className="font-display text-3xl text-emerald-400">{meta.current}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">kg</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Goal</div>
            <div className="font-display text-3xl text-neutral-400">{meta.goal}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">kg</div>
          </div>
        </div>
        {meta.diff != null && (
          <div data-testid="goal-hit-delta" className="text-emerald-400 font-display text-xl tracking-wider mb-6">
            {meta.label === "Lost" ? "−" : "+"}{meta.diff} KG <span className="text-xs text-neutral-500 uppercase">to get here</span>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
        {generating ? (
          <div className="text-neutral-500 mb-4 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Generating share card…
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Goal-hit share card"
            data-testid="goal-hit-preview"
            className="block mx-auto w-44 mb-5 border border-neutral-800"
          />
        ) : null}

        {hit.post_id && (
          <div
            data-testid="goal-hit-squad-strip"
            className={`flex items-center justify-between gap-3 border ${postHidden ? "border-neutral-800 bg-neutral-900/40" : "border-emerald-500/30 bg-emerald-500/5"} p-2.5 mb-3`}
          >
            <div className="flex items-center gap-2 text-left">
              <Users className={`w-4 h-4 ${postHidden ? "text-neutral-500" : "text-emerald-400"}`} />
              <div>
                <div className={`text-[11px] uppercase tracking-widest font-display ${postHidden ? "text-neutral-500" : "text-emerald-400"}`}>
                  {postHidden ? "Post hidden" : "Shared to your Squad"}
                </div>
                <div className="text-[10px] text-neutral-500">
                  {postHidden ? "Buddies & trainers won't see it" : "Buddies & trainers see it live"}
                </div>
              </div>
            </div>
            {!postHidden && (
              <button
                data-testid="goal-hit-hide-post"
                onClick={hidePost}
                disabled={hidingPost}
                className="text-[10px] uppercase tracking-widest font-display text-neutral-400 hover:text-white flex items-center gap-1 px-2 py-1 border border-neutral-700 hover:border-white"
              >
                {hidingPost ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
                Hide
              </button>
            )}
          </div>
        )}

        <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2 flex items-center justify-center gap-2">
          <Target className="w-3 h-3 text-emerald-400" /> Share the moment
        </div>

        <div className="grid grid-cols-3 gap-2 mb-2">
          <Button
            data-testid="goal-hit-share-facebook"
            onClick={shareFacebook}
            disabled={generating}
            className="bg-[#1877F2] hover:bg-[#0e63d0] rounded-none uppercase tracking-wider font-display h-11"
          >
            Facebook
          </Button>
          <Button
            data-testid="goal-hit-share-instagram"
            onClick={shareInstagram}
            disabled={generating}
            className="bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCB045] hover:opacity-90 rounded-none uppercase tracking-wider font-display h-11"
          >
            Instagram
          </Button>
          <Button
            data-testid="goal-hit-share-x"
            onClick={shareTwitter}
            disabled={generating}
            className="bg-black hover:bg-neutral-900 border border-white rounded-none uppercase tracking-wider font-display h-11"
          >
            X
          </Button>
        </div>
        <Button
          data-testid="goal-hit-share-all"
          onClick={shareAll}
          disabled={generating}
          variant="outline"
          className="w-full bg-transparent border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 rounded-none uppercase tracking-wider font-display h-11 mb-2"
        >
          <Download className="w-4 h-4 mr-1.5" /> Save All — Share Anywhere
        </Button>
        <Button
          data-testid="goal-hit-copy-caption"
          onClick={copyCaption}
          variant="ghost"
          size="sm"
          className="w-full text-neutral-400 hover:text-white uppercase tracking-wider font-display h-9"
        >
          <Check className="w-3 h-3 mr-1.5" /> Just copy caption
        </Button>

        <button
          onClick={close}
          data-testid="goal-hit-dismiss"
          className="mt-6 text-xs uppercase tracking-widest text-neutral-500 hover:text-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
